/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import {BatteryAncestor, ListBatteryObject} from '../../../models/ListBattery';
import {NgxEchartsDirective} from 'ngx-echarts';
import {ECharts, EChartsOption, TreeSeriesOption} from 'echarts';
import {IxModule, themeSwitcher} from '@siemens/ix-angular';
import {SchemaListItemDTO} from '../../../models/Schema';
import {TranslateService} from '@ngx-translate/core';
import {PassportService} from '../../../services/passport.service';
import {ThemeService} from '../../../services/theme.service';

const INITIAL_CHART_BUILD_TIME_WAIT = 1000;
/** Base label character limit at zoom level 1. */
const HIERARCHY_LINE_LENGTH = 20;
/** Minimum allowed cutAt so labels never disappear entirely. */
const HIERARCHY_LINE_LENGTH_MIN = 5;
/** Maximum allowed cutAt to prevent extremely long labels. */
const HIERARCHY_LINE_LENGTH_MAX = 80;

/**
 * Escapes HTML-significant characters so attacker-controlled values (e.g. a
 * passport name) cannot inject markup when rendered into the ECharts tooltip,
 * which uses `innerHTML` for the default DOM renderer (CWE-79 / XSS).
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

@Component({
  selector: 'app-hierarchy-graph',
  imports: [NgxEchartsDirective, IxModule],
  templateUrl: './hierarchy-graph.component.html',
  styleUrl: './hierarchy-graph.component.scss',
})
export class HierarchyGraphComponent implements OnInit, OnChanges {
  private readonly translateService = inject(TranslateService);
  private readonly passportService = inject(PassportService);
  private readonly themeService = inject(ThemeService);

  @Input() passport!: ListBatteryObject;
  @Input() ancestor?: BatteryAncestor['data'];
  @Input() descendants!: ListBatteryObject[];
  chart: EChartsOption | null = null;
  schemas: SchemaListItemDTO[] = [];
  loading = true;

  /** Reference to the ECharts instance, set via (chartInit). */
  private chartInstance: ECharts | null = null;
  /** Current zoom level reported by ECharts roam events (1 = default). */
  private currentZoom = 1;

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['descendants'] || changes['ancestor']) {
      await this.loadData();
    }
  }

  ngOnInit() {
    themeSwitcher.schemaChanged.on(() => {
      this.chart = this.buildChart();
    });
    this.translateService.onLangChange.subscribe(() => {
      this.chart = this.buildChart();
    });
    this.loadData(true);
  }

  async loadData(shouldWait = false) {
    this.schemas = (await this.passportService.loadSchemaList()).data;
    if (shouldWait) {
      this.loading = true;
      setTimeout(() => {
        this.chart = this.buildChart();
        this.loading = false;
      }, INITIAL_CHART_BUILD_TIME_WAIT);
    } else {
      this.chart = this.buildChart();
    }
  }

  /**
   * Receives the ECharts instance after the chart is initialised.
   * Keeps a reference for later use in {@link refreshLabels}.
   */
  onChartInit(instance: ECharts) {
    this.chartInstance = instance;
  }

  /**
   * Handles the ECharts 'treeroam' event emitted by ngx-echarts as (chartTreeRoam).
   * Reads the current zoom level from the series option and refreshes label truncation.
   */
  onTreeRoam() {
    if (!this.chartInstance) {
      return;
    }
    const option = this.chartInstance.getOption() as {
      series?: {zoom?: number}[];
    };
    const zoom = option?.series?.[0]?.zoom;
    if (typeof zoom === 'number' && zoom !== this.currentZoom) {
      this.currentZoom = zoom;
      this.refreshLabels();
    }
  }

  /**
   * Recomputes only the formatter-dependent label options and applies them
   * via setOption so the full chart data is not re-rendered.
   */
  private refreshLabels() {
    if (!this.chartInstance) {
      return;
    }
    const stdTextColor = window
      .getComputedStyle(document.body)
      .getPropertyValue('--theme-color-std-text');

    this.chartInstance.setOption({
      series: [
        {
          label: {
            formatter: (params: {name: string; data?: {product: string}}) =>
              this.formatText(params, this.calcCutAt(), '\n', false),
            color: stdTextColor,
          },
          leaves: {
            label: {
              formatter: (params: {name: string; data?: {product: string}}) =>
                this.formatText(params, this.calcCutAt(), '\n', false),
              color: stdTextColor,
            },
          },
        },
      ],
    });
  }

  /**
   * Calculates the character cutAt limit based on how far the current zoom level
   * deviates from the default zoom of 1.
   * The further the zoom is from 1 in either direction, the more characters are shown.
   * At zoom 1 the base value is used; both zooming in and zooming out increase the limit.
   */
  calcCutAt(): number {
    const deviation = Math.abs(this.currentZoom - 1);
    const raw = Math.round(HIERARCHY_LINE_LENGTH * (1 + deviation));
    return Math.min(
      HIERARCHY_LINE_LENGTH_MAX,
      Math.max(HIERARCHY_LINE_LENGTH_MIN, raw),
    );
  }

  getSchemaName(id: string): string {
    const schema = this.schemas.find((_schema) => _schema.id === id);
    if (!schema) {
      return id;
    }
    return schema.name || id;
  }

  buildChart(): EChartsOption {
    const stdTextColor = window
      .getComputedStyle(document.body)
      .getPropertyValue('--theme-color-std-text');

    return {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        formatter: (params: {name: string; data?: {product: string}}) =>
          this.formatText(params),
      } as any,
      series: [
        {
          type: 'tree',
          data: [this.mapHierarchyData()],
          top: '10%',
          bottom: '10%',
          left: '150px',
          right: '150px',
          symbolSize: 7,
          label: {
            color: stdTextColor,
            position: 'left',
            verticalAlign: 'middle',
            align: 'right',
            lineHeight: 15,
            fontSize: 12,
            formatter: (params: {name: string; data?: {product: string}}) =>
              this.formatText(params, this.calcCutAt(), '\n', false),
          },
          leaves: {
            label: {
              position: 'right',
              verticalAlign: 'middle',
              align: 'left',
              color: stdTextColor,
              formatter: (params: {name: string; data?: {product: string}}) =>
                this.formatText(params, this.calcCutAt(), '\n', false),
            },
          },
          emphasis: {
            focus: 'descendant',
          },
          roam: true,
          zoom: 1,
        } as TreeSeriesOption,
      ],
    };
  }

  /**
   * Builds the label/tooltip text for a hierarchy node.
   *
   * @param escapeForHtml When `true` (default), name and product values are
   * HTML-escaped. This MUST stay `true` for any output rendered via `innerHTML`
   * (e.g. the ECharts tooltip) to prevent XSS. Canvas labels are drawn as plain
   * text and pass `false` so entities like "&amp;" are not shown literally.
   */
  formatText(
    params: {name: string; data?: {product: string}},
    cutAt = 200,
    lineBreak = '<br>',
    escapeForHtml = true,
  ): string {
    const render = (value: string): string => {
      const truncated =
        value.length > cutAt ? value.substring(0, cutAt) + '...' : value;
      return escapeForHtml ? escapeHtml(truncated) : truncated;
    };

    const nodeName = params.name;
    const nodeProduct = params?.data?.product;
    if (nodeName && nodeProduct) {
      return `${render(nodeName)}${lineBreak}${render(nodeProduct)}`;
    }
    return nodeName ? render(nodeName) : nodeName;
  }

  getChartTheme() {
    return this.themeService.actualChartTheme;
  }

  mapHierarchyData() {
    const actualPassport = {
      name: this.passport?.metadata?.name ?? this.passport.id,
      product: this.getSchemaName(this.passport?.metadata?.schemaId),
      label: {
        position: ['0', '35px'],
        backgroundColor: window
          .getComputedStyle(document.body)
          .getPropertyValue('--theme-color-3'),
        align: 'center',
        padding: 5,
      },
      itemStyle: {
        color: window
          .getComputedStyle(document.body)
          .getPropertyValue('--theme-btn-outline-primary--color'),
      },
      children: [],
    };
    const ancestors = this.descendants.map((hierarchyItem) => {
      return {
        name: hierarchyItem.metadata?.name ?? hierarchyItem.id,
        product: this.getSchemaName(hierarchyItem.metadata?.schemaId),
      };
    });
    actualPassport.children.push(...(ancestors as never[]));
    if (this.ancestor) {
      return {
        name: this.ancestor.metadata?.name ?? this.ancestor.id,
        product: this.getSchemaName(this.ancestor.metadata?.schemaId),
        children: [actualPassport as never],
      };
    }
    return actualPassport;
  }
}
